import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, AlertTriangle } from 'lucide-react';
import { GenericConnector } from '../generic-connector';
import { Integration } from 'services/integration-service';

/**
 * Generic Connection Component
 * 
 * Provides interface for connecting any integration to the application.
 * Allows users to select connection type (individual or organization)
 * and displays relevant information about the connector.
 * 
 * @param onBack - Callback function to go back to connectors list
 * @param integrationConfig - Configuration object for the integration
 * @param existingIntegration - Existing integration if any
 * @returns A generic connection interface
 */

interface IntegrationConfig {
    id: string;
    name: string;
    description: string;
    longDescription?: string;
    icon: string;
    iconAlt: string;
    category: string;
    developer: string;
    website?: string;
    privacyPolicy?: string;
}

interface GenericConnectionProps {
    onBack: () => void;
    integrationConfig: IntegrationConfig;
    existingIntegration?: Integration;
}

interface ConnectionType {
    id: 'user' | 'organization';
    label: string;
    selected: boolean;
}

const RadioButton = ({ selected }: { selected: boolean }) => (
    <div className="relative size-5 flex items-center justify-center">
        <div className={`w-5 h-5 rounded-full border-2 ${selected ? 'border-[#ac9578]' : 'border-neutral-300'}`}>
            {selected && (
                <div className="w-full h-full rounded-full flex items-center justify-center">
                    <div className="w-2 h-2 rounded-full bg-[#ac9578]" />
                </div>
            )}
        </div>
    </div>
);

export const GenericConnection = ({
    onBack,
    integrationConfig,
    existingIntegration
}: GenericConnectionProps) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { t } = useTranslation();

    // Initialize connection types based on existing integration scope
    const getInitialConnectionTypes = (): ConnectionType[] => {
        const existingScope = existingIntegration?.scope as 'user' | 'organization' | undefined;
        return [
            { id: 'user', label: 'CONNECT_FOR_INDIVIDUAL_ACCOUNT', selected: existingScope === 'user' || !existingScope },
            { id: 'organization', label: 'CONNECT_FOR_ORGANIZATION_ACCOUNT', selected: existingScope === 'organization' }
        ];
    };

    const [connectionTypes, setConnectionTypes] = useState<ConnectionType[]>(getInitialConnectionTypes());

    const handleConnectionTypeSelect = (id: 'user' | 'organization') => {
        setConnectionTypes(prev =>
            prev.map(type => ({ ...type, selected: type.id === id }))
        );
    };

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const handleConnect = () => {
        const selectedType = connectionTypes.find(type => type.selected);
        // TODO: Implement actual connection logic
        console.log('Connecting to', integrationConfig.name, 'with type:', selectedType?.id);
    };

    return (
        <div className="px-4 sm:px-6 md:px-8 py-4 sm:py-5 space-y-6 sm:space-y-8 md:h-full xs:h-[560px] xs:overflow-auto bg-background">
            {/* Header with Back Button */}
            <button
                onClick={onBack}
                className="flex items-center gap-[5px] text-[#ac9578] hover:text-[#9a8469] transition-colors"
            >
                <ChevronLeft className="w-4 h-4" />
                <span className="text-[14px] font-medium leading-[22px]">back</span>
            </button>

            {/* Main Content */}
            <div className="space-y-6 sm:space-y-8">
                {/* Header Section with Integration Info */}
                <div className="pb-4 border-b border-light-gray">
                    <div className="flex items-center gap-3">
                        <div className="w-[48px] h-[48px] sm:w-[52px] sm:h-[52px] border border-light-gray rounded-lg flex items-center justify-center p-3">
                            <img src={integrationConfig.icon} alt={integrationConfig.iconAlt} className="w-5 h-5 sm:w-6 sm:h-6" />
                        </div>
                        <div className="space-y-1 flex-1">
                            <h3 className="text-[15px] sm:text-[16px] font-bold text-foreground leading-[1.4]">
                                {integrationConfig.name}
                            </h3>
                            <p className="text-[11px] sm:text-[12px] text-foreground leading-[1.4] tracking-[-0.24px]">
                                {t(integrationConfig.description)}
                            </p>
                        </div>
                    </div>
                </div>

                {/* Connection Type Selection */}
                <div className="space-y-3">
                    <h4 className="text-[13px] font-medium text-foreground leading-[1.25]">
                        {t('SELECT_CONNECTION_TYPE')}
                    </h4>
                    <div className="flex flex-col sm:flex-row gap-3">
                        {connectionTypes.map((type) => (
                            <button
                                key={type.id}
                                onClick={() => handleConnectionTypeSelect(type.id)}
                                className="flex-1 sm:max-w-[230px] p-3 sm:p-4 border border-light-gray rounded-lg flex items-center gap-2 hover:border-border transition-colors"
                            >
                                <RadioButton selected={type.selected} />
                                <span className="text-[11px] sm:text-[12px] text-foreground leading-[1.4] tracking-[-0.24px] text-left">
                                    {t(type.label)}
                                </span>
                            </button>
                        ))}
                    </div>

                    {/* Connection Type Warnings */}
                    {connectionTypes.find(type => type.id === 'user')?.selected && (
                        <div className="flex items-start gap-2 p-3 sm:p-4 bg-[hsl(var(--warning-background))] border border-[hsl(var(--warning))] rounded-lg">
                            <AlertTriangle className="w-4 h-4 text-[hsl(var(--primary-500))] flex-shrink-0 mt-0.5" />
                            <p className="text-[11px] sm:text-[12px] text-[hsl(var(--warning-high-emphasis))] leading-[1.6] tracking-[-0.24px]">
                                {t('ONLY_YOU_WILL_HAVE_ACCESS_TO_THE_CONNECTED_FILES')}
                            </p>
                        </div>
                    )}

                    {connectionTypes.find(type => type.id === 'organization')?.selected && (
                        <div className="flex items-start gap-2 p-3 sm:p-4 bg-[hsl(var(--warning-background))] border border-[hsl(var(--warning))] rounded-lg">
                            <AlertTriangle className="w-4 h-4 text-[hsl(var(--primary-500))] flex-shrink-0 mt-0.5" />
                            <p className="text-[11px] sm:text-[12px] text-[hsl(var(--warning-high-emphasis))] leading-[1.6] tracking-[-0.24px]">
                                {t('AVAILABLE_TO_ALL_ORGANIZATION_MEMBERS')}
                            </p>
                        </div>
                    )}
                </div>

                {/* Description Section */}
                <div className="space-y-3">
                    <h4 className="text-[13px] font-medium text-foreground leading-[1.25]">
                        {t('DESCRIPTION')}
                    </h4>
                    <div className="p-3 sm:p-4 border border-light-gray rounded-lg min-h-[156px] flex items-start">
                        <p className="text-[11px] sm:text-[12px] text-foreground leading-[1.6] tracking-[-0.24px]">
                            {t(integrationConfig.longDescription || integrationConfig.description)}
                        </p>
                    </div>
                </div>
            </div>

            {/* Footer with Connect Button */}
            <div className="pt-4 border-t border-light-gray flex justify-center sm:justify-end">
                <GenericConnector
                    existingIntegration={existingIntegration}
                    integrationType={integrationConfig.id}
                    integrationName={integrationConfig.name}
                    scope={connectionTypes.find(type => type.selected)?.id || 'user'}
                />
            </div>
        </div>
    );
};
